import { Anchor, Badge, Button, Group, SegmentedControl, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UnifyTable, type UnifyTableColumn, type UnifyTableMultiFilterConfig } from '@/components/UnifyTable';
import { AddEraModal } from './components/AddEraModal';
import { formatCurrency, formatDateTime } from './components/billingFormatters';
import {
  buildRemittanceListRows,
  filterRemittanceListRows,
  type RemittanceListRow,
  type RemittanceOutcomeFilter,
} from './mock/mockRemittanceList';

const OUTCOME_FILTER_OPTIONS: { label: string; value: RemittanceOutcomeFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Paid', value: 'paid' },
  { label: 'Denied', value: 'denied' },
  { label: 'Unmatched', value: 'unmatched' },
];

const OUTCOME_COLORS: Record<RemittanceListRow['displayOutcome'], string> = {
  paid: 'green',
  partial: 'cyan',
  denied: 'red',
  unmatched: 'orange',
};

const DENIAL_STATUS_COLORS = {
  open: 'red',
  appealing: 'orange',
  corrected: 'blue',
  closed: 'gray',
} as const;

function parseOutcomeFilter(value: string | null): RemittanceOutcomeFilter {
  if (value === 'paid' || value === 'denied' || value === 'unmatched') return value;
  return 'all';
}

export function RemittancesPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addEraOpened, { open: openAddEra, close: closeAddEra }] = useDisclosure(false);
  const outcomeFilter = parseOutcomeFilter(searchParams.get('outcome'));
  const [rows] = useState(() => buildRemittanceListRows());

  const filteredRows = useMemo(
    () => filterRemittanceListRows(rows, outcomeFilter),
    [rows, outcomeFilter],
  );

  const columns = useMemo(
    (): UnifyTableColumn<RemittanceListRow>[] => [
      {
        key: 'date',
        label: 'Date',
        render: (row) => <Text size="sm">{formatDateTime(row.date)}</Text>,
        getValue: (row) => row.date,
      },
      {
        key: 'kind',
        label: 'Type',
        render: (row) => (
          <Badge variant="light" size="sm" color={row.kind === 'era' ? 'grape' : 'gray'}>
            {row.kind === 'claim' ? 'Claim' : row.kind === 'era' ? 'ERA' : 'Ack reject'}
          </Badge>
        ),
        getValue: (row) => row.kind,
      },
      {
        key: 'clientName',
        label: 'Client',
        render: (row) => (
          <Text size="sm" fw={row.clientName === '—' ? 400 : 600}>
            {row.clientName}
          </Text>
        ),
        getValue: (row) => row.clientName,
      },
      {
        key: 'payer',
        label: 'Payer',
        getValue: (row) => row.payer,
      },
      {
        key: 'displayOutcome',
        label: 'Outcome',
        render: (row) => (
          <Badge variant="light" size="sm" color={OUTCOME_COLORS[row.displayOutcome]}>
            {row.displayOutcome}
          </Badge>
        ),
        getValue: (row) => row.displayOutcome,
      },
      {
        key: 'amountPaid',
        label: 'Paid',
        align: 'right',
        render: (row) => <Text size="sm">{formatCurrency(row.amountPaid)}</Text>,
        getValue: (row) => String(row.amountPaid),
      },
      {
        key: 'matchStatus',
        label: 'Match',
        render: (row) =>
          row.matchStatus ? (
            <Badge
              variant="light"
              size="sm"
              color={row.matchStatus === 'matched' ? 'green' : row.matchStatus === 'partial' ? 'yellow' : 'red'}
            >
              {row.matchStatus}
            </Badge>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ),
        getValue: (row) => row.matchStatus ?? '',
      },
      {
        key: 'carc',
        label: 'CARC',
        getValue: (row) => row.carc ?? '',
      },
      {
        key: 'denialStatus',
        label: 'Work status',
        render: (row) =>
          row.denialStatus ? (
            <Badge variant="light" size="sm" color={DENIAL_STATUS_COLORS[row.denialStatus]}>
              {row.denialStatus}
            </Badge>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ),
        getValue: (row) => row.denialStatus ?? '',
      },
      {
        key: 'reason',
        label: 'Reason',
        getValue: (row) => row.reason ?? '',
      },
      {
        key: 'links',
        label: 'Links',
        sortable: false,
        render: (row) => (
          <Group gap={6} wrap="wrap" onClick={(e) => e.stopPropagation()}>
            {row.claimId && (
              <Anchor
                component={Link}
                to={`/dashboard/billing/claims/${encodeURIComponent(row.claimId)}`}
                size="xs"
              >
                Claim
              </Anchor>
            )}
            {row.eobId && (
              <Anchor
                component={Link}
                to={`/dashboard/billing/eobs/${encodeURIComponent(row.eobId)}`}
                size="xs"
              >
                EOB
              </Anchor>
            )}
            {row.eraId && row.kind === 'era' && (
              <Text size="xs" c="dimmed">
                {row.eraId}
              </Text>
            )}
          </Group>
        ),
        getValue: (row) => [row.claimId, row.eobId, row.eraId].filter(Boolean).join(' '),
      },
    ],
    [],
  );

  const multiFilters = useMemo(
    (): UnifyTableMultiFilterConfig[] => [
      {
        columnKey: 'displayOutcome',
        allLabel: 'All outcomes',
        placeholder: 'Outcome',
        data: ['paid', 'partial', 'denied', 'unmatched'],
        emptyMeansAll: true,
      },
      {
        columnKey: 'matchStatus',
        allLabel: 'All match statuses',
        placeholder: 'Match',
        data: ['matched', 'partial', 'unmatched'],
        emptyMeansAll: true,
      },
      {
        columnKey: 'denialStatus',
        allLabel: 'All work statuses',
        placeholder: 'Work status',
        data: ['open', 'appealing', 'corrected', 'closed'],
        emptyMeansAll: true,
      },
    ],
    [],
  );

  return (
    <>
      <UnifyTable
        title="Remittances"
        subtitle="ERA inbox and adjudication outcomes"
        columns={columns}
        data={filteredRows}
        getRowKey={(row) => row.id}
        searchable
        searchPlaceholder="Search by client, payer, reason, payment ref…"
        sortable
        pagination
        defaultRowsPerPage={10}
        multiFilters={multiFilters}
        extraFilters={
          <SegmentedControl
            size="xs"
            data={OUTCOME_FILTER_OPTIONS}
            value={outcomeFilter}
            onChange={(value) => {
              const next = parseOutcomeFilter(value);
              if (next === 'all') {
                setSearchParams({});
              } else {
                setSearchParams({ outcome: next });
              }
            }}
          />
        }
        headerRight={
          <Button color="gold" leftSection={<IconPlus size={16} />} onClick={openAddEra}>
            Add ERA
          </Button>
        }
        onRowClick={(row) => {
          if (row.eobId) {
            navigate(`/dashboard/billing/eobs/${encodeURIComponent(row.eobId)}`);
          }
        }}
      />
      <AddEraModal opened={addEraOpened} onClose={closeAddEra} />
    </>
  );
}
